import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import api, { fileUrl } from "@/lib/api";
import { getPreset, SHOWCASE_LANG_LABEL } from "@/lib/showcasePresets";
import { Loader2, Star, Instagram, Facebook, MapPin, Phone, Clock, UtensilsCrossed, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>
    {children}
  </motion.div>
);

const ParallaxBg = ({ src, overlay, children, minH = "80vh" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <div ref={ref} className="relative overflow-hidden" style={{ minHeight: minH }}>
      {src && <motion.div style={{ y, backgroundImage: `url(${fileUrl(src)})`, backgroundSize: "cover", backgroundPosition: "center" }} className="absolute inset-0 scale-125" />}
      <div className="absolute inset-0" style={{ background: overlay }} />
      <div className="relative z-10">{children}</div>
    </div>
  );
};

const CookieBanner = ({ theme, slug }) => {
  const [show, setShow] = useState(false);
  useEffect(() => { setShow(!localStorage.getItem("sc_cookie_ok")); }, []);
  if (!show) return null;
  return (
    <div className="fixed bottom-0 inset-x-0 z-50 p-4" data-testid="cookie-banner">
      <div className="max-w-3xl mx-auto rounded-2xl p-4 flex flex-col sm:flex-row items-center gap-3 shadow-2xl" style={{ backgroundColor: theme.surface, color: theme.text, border: `1px solid ${theme.accent}40` }}>
        <p className="text-sm flex-1">Questo sito usa cookie tecnici e raccoglie i dati inviati tramite il form di prenotazione. Consulta la <Link to={`/s/${slug}/privacy`} className="underline" style={{ color: theme.accent }}>privacy policy</Link>.</p>
        <button onClick={() => { localStorage.setItem("sc_cookie_ok", "1"); setShow(false); }} data-testid="cookie-accept" className="rounded-full px-5 py-2 text-sm font-semibold whitespace-nowrap" style={{ backgroundColor: theme.accent, color: theme.dark ? "#0B0B0C" : "#fff" }}>Accetto</button>
      </div>
    </div>
  );
};

const BookingForm = ({ slug, theme }) => {
  const [f, setF] = useState({ name: "", contact: "", date: "", time: "", people: 2, notes: "", company: "" });
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.contact.trim()) return toast.error("Inserisci nome e contatto");
    setSending(true);
    try {
      await api.post(`/showcase/${slug}/booking`, { ...f, people: Number(f.people) });
      toast.success("Richiesta inviata! Ti contatteremo a breve.");
      setF({ name: "", contact: "", date: "", time: "", people: 2, notes: "", company: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Invio non riuscito"); } finally { setSending(false); }
  };
  const inp = "w-full rounded-xl px-4 py-3 text-sm focus:outline-none";
  const inpStyle = { backgroundColor: theme.dark ? "rgba(255,255,255,0.06)" : "#fff", color: theme.text, border: `1px solid ${theme.accent}33` };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="booking-form">
      <input aria-hidden="true" tabIndex={-1} autoComplete="off" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} name="company" className="hidden" data-testid="booking-honeypot" />
      <div className="grid sm:grid-cols-2 gap-3">
        <input className={inp} style={inpStyle} placeholder="Nome" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="booking-name" />
        <input className={inp} style={inpStyle} placeholder="Telefono o email" value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} data-testid="booking-contact" />
        <input type="date" className={inp} style={inpStyle} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="booking-date" />
        <input type="time" className={inp} style={inpStyle} value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid="booking-time" />
        <input type="number" min="1" max="100" className={inp} style={inpStyle} placeholder="Persone" value={f.people} onChange={(e) => setF({ ...f, people: e.target.value })} data-testid="booking-people" />
      </div>
      <textarea className={inp} style={inpStyle} rows={3} placeholder="Note (facoltative)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="booking-notes" />
      <button type="submit" disabled={sending} data-testid="booking-submit" className="rounded-full px-6 py-3 text-sm font-semibold flex items-center gap-2 transition-transform hover:scale-[1.02]" style={{ backgroundColor: theme.accent, color: theme.dark ? "#0B0B0C" : "#fff" }}>{sending && <Loader2 className="w-4 h-4 animate-spin" />} Invia richiesta</button>
    </form>
  );
};

const SectionTitle = ({ children, theme }) => (
  <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: theme.heading, color: theme.accent }}>{children}</h2>
);

const ShowcaseSite = () => {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const preview = params.get("preview");
  const [site, setSite] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lang, setLang] = useState("it");

  useEffect(() => {
    const url = `/showcase/${slug}${preview ? `?preview=${preview}` : ""}`;
    api.get(url).then((r) => setSite(r.data)).catch(() => setNotFound(true));
    if (!preview) api.post(`/showcase/${slug}/view`).catch(() => {});
  }, [slug, preview]);

  useEffect(() => {
    if (!site) return;
    const seo = site.seo || {};
    const T = site.translations || {};
    const trv = (key, base) => (lang !== "it" && T[lang]?.[key]) || base;
    const title = trv("seo.title", seo.title) || site.name;
    const descr = trv("seo.description", seo.description);
    document.title = title;
    const setMeta = (attr, key, val) => {
      if (!val) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
    };
    setMeta("name", "description", descr);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", descr);
    if (seo.og_image) setMeta("property", "og:image", fileUrl(seo.og_image));
    if (seo.favicon) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
      link.href = fileUrl(seo.favicon);
    }
  }, [site, lang]);

  if (notFound) return <div className="min-h-screen flex items-center justify-center bg-[#0B0B0C] text-white/70">Sito non disponibile.</div>;
  if (!site) return <div className="min-h-screen flex items-center justify-center bg-[#0B0B0C] text-[#C6A867] gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Caricamento…</div>;

  const theme = getPreset(site.preset);
  const c = site.content || {};
  const langs = site.languages || ["it"];
  const T = site.translations || {};
  const tr = (path, base) => (lang === "it" ? base : (T[lang]?.[path] || base));
  const wa = (c.contact?.whatsapp || "").replace(/[^0-9]/g, "");

  return (
    <div style={{ backgroundColor: theme.bg, color: theme.text, fontFamily: theme.body }} data-testid="showcase-site">
      {site.status !== "published" && <div className="text-center text-xs py-1.5 font-semibold" style={{ backgroundColor: theme.accent, color: theme.dark ? "#0B0B0C" : "#fff" }} data-testid="preview-banner">ANTEPRIMA — bozza non pubblicata</div>}

      {langs.length > 1 && (
        <div className="fixed top-3 right-3 z-40">
          <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="showcase-lang-select" className="rounded-full px-3 py-1.5 text-xs font-semibold shadow-lg focus:outline-none cursor-pointer" style={{ backgroundColor: theme.surface, color: theme.text, border: `1px solid ${theme.accent}55` }}>
            {langs.map((l) => <option key={l} value={l} style={{ color: "#111" }}>{SHOWCASE_LANG_LABEL[l] || l}</option>)}
          </select>
        </div>
      )}

      {/* HERO */}
      <ParallaxBg src={c.hero?.bg_image} overlay={theme.overlay} minH="88vh">
        <div className="max-w-4xl mx-auto px-6 min-h-[88vh] flex flex-col items-center justify-center text-center">
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-4xl sm:text-6xl lg:text-7xl leading-tight" style={{ fontFamily: theme.heading, color: "#fff" }}>{tr("hero.title", c.hero?.title) || site.name}</motion.h1>
          {c.hero?.subtitle && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="mt-4 text-lg text-white/85 max-w-2xl">{tr("hero.subtitle", c.hero.subtitle)}</motion.p>}
          {c.hero?.button_text && <motion.a href={c.hero?.button_link || "#contatti"} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }} className="mt-8 rounded-full px-8 py-3.5 font-semibold transition-transform hover:scale-105" style={{ backgroundColor: theme.accent, color: theme.dark ? "#0B0B0C" : "#fff" }} data-testid="hero-cta">{tr("hero.button_text", c.hero.button_text)}</motion.a>}
        </div>
      </ParallaxBg>

      {/* ABOUT */}
      {(c.about?.text || c.about?.image) && (
        <section className="max-w-5xl mx-auto px-6 py-20 sm:py-28 grid md:grid-cols-2 gap-10 items-center">
          <Reveal>{c.about?.image && <img src={fileUrl(c.about.image)} alt={c.about?.title} className="w-full h-full object-cover shadow-xl" style={{ borderRadius: theme.radius }} loading="lazy" />}</Reveal>
          <Reveal delay={0.1}>
            <SectionTitle theme={theme}>{tr("about.title", c.about?.title) || "Chi siamo"}</SectionTitle>
            <p className="text-base leading-relaxed whitespace-pre-line" style={{ color: theme.muted }}>{tr("about.text", c.about?.text)}</p>
          </Reveal>
        </section>
      )}

      {/* MENU link — solo se il cliente ha un menù su questa piattaforma */}
      {site.menu_slug && (
        <section className="max-w-5xl mx-auto px-6 pb-8 text-center">
          <Reveal><a href={`/m/${site.menu_slug}`} target="_blank" rel="noopener noreferrer" data-testid="menu-link" className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 font-semibold transition-transform hover:scale-105" style={{ border: `1.5px solid ${theme.accent}`, color: theme.accent }}><UtensilsCrossed className="w-5 h-5" /> {tr("menu.button_text", c.menu?.button_text) || "Guarda il menù"}</a></Reveal>
        </section>
      )}

      {/* GALLERY */}
      {c.gallery?.images?.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 py-16">
          <Reveal><SectionTitle theme={theme}>Galleria</SectionTitle></Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {c.gallery.images.map((img, i) => (
              <Reveal key={i} delay={(i % 3) * 0.08}><img src={fileUrl(img)} alt="" className="w-full h-56 object-cover transition-transform duration-500 hover:scale-105" style={{ borderRadius: theme.radius }} loading="lazy" /></Reveal>
            ))}
          </div>
        </section>
      )}

      {/* REVIEWS */}
      {c.reviews?.google_url && (
        <section className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Reveal><a href={c.reviews.google_url} target="_blank" rel="noopener noreferrer" data-testid="reviews-link" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold transition-transform hover:scale-105" style={{ backgroundColor: theme.surface, color: theme.accent, border: `1px solid ${theme.accent}44` }}><Star className="w-5 h-5" /> {tr("reviews.text", c.reviews?.text) || "Leggi le recensioni su Google"}</a></Reveal>
        </section>
      )}

      {/* CONTACT + BOOKING */}
      <section id="contatti" className="py-20" style={{ backgroundColor: theme.surface }}>
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-10">
          <Reveal>
            <SectionTitle theme={theme}>Contatti</SectionTitle>
            <div className="space-y-3 text-sm" style={{ color: theme.muted }}>
              {c.contact?.address && <p className="flex items-center gap-2"><MapPin className="w-4 h-4" style={{ color: theme.accent }} /> {c.contact.address}</p>}
              {c.contact?.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4" style={{ color: theme.accent }} /> {c.contact.phone}</p>}
              {c.contact?.hours && <p className="flex items-center gap-2"><Clock className="w-4 h-4" style={{ color: theme.accent }} /> {tr("contact.hours", c.contact.hours)}</p>}
            </div>
            {c.contact?.map_embed && <div className="mt-4 overflow-hidden" style={{ borderRadius: theme.radius }} dangerouslySetInnerHTML={{ __html: c.contact.map_embed }} />}
          </Reveal>
          {c.contact?.booking_enabled !== false && (
            <Reveal delay={0.1}>
              <SectionTitle theme={theme}>Prenota</SectionTitle>
              <BookingForm slug={slug} theme={theme} />
            </Reveal>
          )}
        </div>
      </section>

      {/* SOCIAL + FOOTER */}
      <footer className="py-12 text-center" style={{ backgroundColor: theme.bg }}>
        <div className="flex items-center justify-center gap-4 mb-4">
          {c.social?.instagram && <a href={c.social.instagram} target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-110" style={{ color: theme.accent }}><Instagram className="w-6 h-6" /></a>}
          {c.social?.facebook && <a href={c.social.facebook} target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-110" style={{ color: theme.accent }}><Facebook className="w-6 h-6" /></a>}
          {c.social?.tiktok && <a href={c.social.tiktok} target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-110 text-sm font-bold" style={{ color: theme.accent }}>TikTok</a>}
        </div>
        <p className="text-xs" style={{ color: theme.muted }}>{tr("footer.text", c.footer?.text) || site.name} · <Link to={`/s/${slug}/privacy`} className="underline">Privacy</Link></p>
      </footer>

      {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-btn" className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-transform hover:scale-110" style={{ backgroundColor: "#25D366", color: "#fff" }}><MessageCircle className="w-7 h-7" /></a>}
      <CookieBanner theme={theme} slug={slug} />
    </div>
  );
};

export default ShowcaseSite;
